import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { ARTWORKS } from "../../data/artworks";
import { Eyebrow, MaskLines, Reveal } from "./Reveal";
import { useT } from "../../lib/content";

const empty = { name: "", email: "", phone: "", artwork: "", message: "" };
const field = "w-full bg-transparent border-0 border-b border-[#15202B]/30 focus:border-[#15202B] outline-none py-3 text-[#15202B] placeholder:text-[#6B7782] transition-colors";

export const Contact = ({ items = ARTWORKS, inquiry }) => {
  const [f, setF] = useState(empty);
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const t = useT();
  useEffect(() => {
    if (!inquiry) return;
    setState("idle");
    setF((p) => ({ ...p, artwork: inquiry.vendu ? "Commande sur mesure" : inquiry.t, message: inquiry.vendu
      ? `Bonjour Christine, l'œuvre « ${inquiry.t} » est vendue. Serait-il possible d'en créer une semblable ?`
      : `Bonjour Christine, j'aimerais réserver l'œuvre « ${inquiry.t} ».` }));
  }, [inquiry]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !/^\S+@\S+\.\S+$/.test(f.email) || !f.message.trim()) { setErr("Remplissez votre nom, un courriel valide et votre message."); return; }
    setErr(""); setState("loading");
    try { await api.post("/contact", f); setState("done"); setF(empty); }
    catch (ex) { setErr(apiError(ex)); setState("idle"); }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 lg:py-40" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-12">
        <div className="lg:col-span-5">
          <Eyebrow>{t("contact_eyebrow")}</Eyebrow>
          <h2 className="f-display mt-5 text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.98] text-[#15202B]">
            <MaskLines inView lines={[t("contact_title1"), <em key="e">{t("contact_title2")}</em>]} />
          </h2>
          <Reveal className="mt-8 text-[#2E3B47] text-base sm:text-lg font-light leading-relaxed max-w-md">
            {t("contact_intro")}
          </Reveal>
          <Reveal delay={0.1} className="mt-10 grid gap-1 text-sm">
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#8A6420]">{t("contact_place_label")}</span>
            <span className="text-[#15202B]">{t("contact_place")}</span>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 relative">
          <AnimatePresence mode="popLayout">
            {state === "done" ? (
              <motion.div key="ok" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="bg-[#E4ECE7] p-10 sm:p-14" data-testid="contact-success-message">
                <span className="w-14 h-14 rounded-full bg-[#15202B] text-[#F6F1E9] grid place-items-center"><Check className="w-6 h-6" /></span>
                <p className="f-display text-4xl mt-6 text-[#15202B]">Merci, votre message est bien reçu.</p>
                <p className="mt-3 text-[#2E3B47]">Christine vous répondra par courriel très bientôt.</p>
                <button onClick={() => setState("idle")} className="btn-line mt-8" data-testid="contact-new-message-button">Envoyer un autre message</button>
              </motion.div>
            ) : (
              <motion.form key="form" onSubmit={submit} noValidate initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="grid gap-7" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-7">
                  <input className={field} placeholder="Votre nom *" value={f.name} onChange={set("name")} autoComplete="name" data-testid="contact-input-name" aria-label="Nom" />
                  <input className={field} type="email" placeholder="Courriel *" value={f.email} onChange={set("email")} autoComplete="email" data-testid="contact-input-email" aria-label="Courriel" />
                </div>
                <div className="grid sm:grid-cols-2 gap-7">
                  <input className={field} placeholder="Téléphone (facultatif)" value={f.phone} onChange={set("phone")} autoComplete="tel" data-testid="contact-input-phone" aria-label="Téléphone" />
                  <select className={`${field} cursor-pointer`} value={f.artwork} onChange={set("artwork")} data-testid="contact-select-artwork" aria-label="Œuvre concernée">
                    <option value="">Œuvre concernée (facultatif)</option>
                    <option value="Commande sur mesure">Commande sur mesure</option>
                    {items.filter((a) => !a.vendu).map((a) => <option key={a.id} value={a.t}>{a.t}</option>)}
                  </select>
                </div>
                <textarea className={`${field} min-h-[150px] resize-y`} placeholder="Parlez-moi de votre projet *" value={f.message} onChange={set("message")} data-testid="contact-textarea-message" aria-label="Message" />
                {err && <p className="text-sm text-[#A2372F]" data-testid="contact-error-message">{err}</p>}
                <button type="submit" disabled={state === "loading"} className="btn-ink justify-self-start" data-testid="contact-submit-button">
                  {state === "loading" ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : "Envoyer le message"}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
